// src/features/fourlink-config/hooks/useDataFetching.js

import { useState, useEffect, useMemo } from 'react';
import {
  fetchTareasRegistros,
  fetchTareasRegistrosWithChecklist,
  fetchActivos,
  fetchCategoriaActivos,
  fetchCategoriaProfesional,
  fetchCategoriaProfesionalGrupos,
  fetchCategorias,
  fetchEquipos,
  fetchPlanes,
  fetchTipologiasCentro,
  fetchChecklists,
} from '../api';
import {
  flattenApiCollection,
  filterCollectionByInstance,
  mergeTareasCollections,
} from '../utils/normalization/dataTransformers';
import { ensureArray, firstNonEmpty } from '../utils/helpers/filters';

const normalizeName = (entry, fallback = '') => {
  if (!entry) return fallback;
  if (typeof entry === 'string') return entry.trim();
  if (typeof entry === 'object') {
    const candidate = firstNonEmpty(
      entry.nombre,
      entry.name,
      entry.titulo,
      entry.label,
      entry.descripcion,
      entry.descripcion_larga,
      entry.texto,
    );
    return typeof candidate === 'string' ? candidate.trim() : fallback;
  }
  return fallback;
};

const sortByOrderThenName = (collection) =>
  [...collection].sort(
    (a, b) =>
      (a?.orden ?? Number.POSITIVE_INFINITY) - (b?.orden ?? Number.POSITIVE_INFINITY) ||
      normalizeName(a).localeCompare(normalizeName(b), 'es', { sensitivity: 'base' }),
  );

export const useDataFetching = (selectedInstance, apiOverrides) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    tipologias: [],
    equipos: [],
    categoriasProfesionales: {},
    planes: [],
    activosPorCategoria: {},
    tareas: {},
  });

  const api = useMemo(
    () => ({
      fetchTareasRegistros,
      fetchTareasRegistrosWithChecklist,
      fetchActivos,
      fetchCategoriaActivos,
      fetchCategoriaProfesional,
      fetchCategoriaProfesionalGrupos,
      fetchCategorias,
      fetchEquipos,
      fetchPlanes,
      fetchTipologiasCentro,
      fetchChecklists,
      ...(apiOverrides || {}),
    }),
    [apiOverrides],
  );

  const selectedInstanceId = selectedInstance?.id ? String(selectedInstance.id).trim() : '';

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const [
          tareasRegistrosData,
          tareasRegistrosChecklistData,
          activosData,
          categoriaActivosData,
          categoriasProfesionalesData,
          categoriasProfesionalesGruposData,
          categoriasData,
          equiposData,
          planesData,
          tipologiasCentroData,
          checklistsData,
        ] = await Promise.all([
          api.fetchTareasRegistros(),
          api.fetchTareasRegistrosWithChecklist().catch(() => null),
          api.fetchActivos(),
          api.fetchCategoriaActivos(),
          api.fetchCategoriaProfesional(),
          api.fetchCategoriaProfesionalGrupos(),
          api.fetchCategorias(),
          api.fetchEquipos(),
          api.fetchPlanes(),
          api.fetchTipologiasCentro(),
          api.fetchChecklists().catch(() => []),
        ]);

        const tipologias = sortByOrderThenName(
          filterCollectionByInstance(flattenApiCollection(tipologiasCentroData), selectedInstanceId),
        )
          .map((t) => normalizeName(t))
          .filter(Boolean);

        const equipos = sortByOrderThenName(
          filterCollectionByInstance(flattenApiCollection(equiposData), selectedInstanceId),
        )
          .map((t) => normalizeName(t))
          .filter(Boolean);

        const planes = sortByOrderThenName(
          filterCollectionByInstance(flattenApiCollection(planesData), selectedInstanceId),
        )
          .map((t) => normalizeName(t))
          .filter(Boolean);

        // Catalogo de categorias de tareas (id -> nombre, orden)
        const categoriasCatalogo = new Map();
        const categoriaOrden = new Map();
        sortByOrderThenName(flattenApiCollection(categoriasData)).forEach((cat, idx) => {
          const id = firstNonEmpty(cat.id, cat.ID, cat.categoria_id, cat.id_categoria, cat.id_categoria_TR, cat.id_categoria_tr);
          const nombre = normalizeName(cat);
          const orden = cat?.orden ?? Number.POSITIVE_INFINITY;
          if (id) {
            categoriasCatalogo.set(String(id), { nombre: nombre || '', orden });
          }
        });

        const checklistsCatalog = new Map(
          flattenApiCollection(checklistsData).map((item) => {
            const id = firstNonEmpty(item.id, item.ID);
            return [id ? String(id) : '', { id: id ? String(id) : '', nombre: normalizeName(item) }];
          }),
        );

        // Catalogo de categorias de activos (cuando vienen como lista separada)
        const categoriaActivosCatalogo = new Map();
        const categoriaActivosOrden = new Map();
        sortByOrderThenName(flattenApiCollection(categoriaActivosData)).forEach((cat, idx) => {
          const id = firstNonEmpty(cat.id, cat.ID, cat.categoria_id);
          const nombre = normalizeName(cat);
          const orden = cat?.orden ?? Number.POSITIVE_INFINITY;
          if (id) {
            categoriaActivosCatalogo.set(String(id), { nombre, orden });
          }
        });

        const rawActivosCollection = Array.isArray(activosData?.data)
          ? activosData.data
          : Array.isArray(activosData)
            ? activosData
            : flattenApiCollection(activosData);

        const activosPorCategoria = {};

        const matchesInstance = (entry) => {
          if (!entry || !selectedInstanceId) return true;
          const fk = firstNonEmpty(
            entry.fk_instancia,
            entry.fkInstancia,
            entry.instancia_id,
            entry.instanciaId,
            entry.id_instancia,
            entry.instance_id,
            entry.instanceId,
            entry.instancia,
          );
          return !fk || String(fk).trim() === selectedInstanceId;
        };

        const categoriasActivosOrdenadas = sortByOrderThenName(
          rawActivosCollection
            .map((cat) => ({
              ...cat,
              nombre: normalizeName(cat, 'Sin categoria'),
              orden: cat?.orden ?? Number.POSITIVE_INFINITY,
              activos: ensureArray(cat?.activos).map((a) => ({
                ...a,
                nombre: normalizeName(a),
                orden: a?.orden ?? Number.POSITIVE_INFINITY,
              })),
            }))
            .filter((cat) => matchesInstance(cat)),
        );

        if (categoriasActivosOrdenadas.length > 0) {
          categoriasActivosOrdenadas.forEach((cat) => {
            const listaOrdenada = sortByOrderThenName(cat.activos || [])
              .map((a) => a.nombre)
              .filter(Boolean);
            activosPorCategoria[cat.nombre || 'Sin categoria'] = listaOrdenada;
          });
        } else {
          // Fallback a la lÛgica anterior si el endpoint no devuelve jerarquÌa
          filterCollectionByInstance(flattenApiCollection(activosData), selectedInstanceId).forEach((activo) => {
            const nombre = normalizeName(activo);
            const categoriaNombre =
              firstNonEmpty(
                activo.categoria,
                categoriaActivosCatalogo.get(firstNonEmpty(activo.categoria_id, activo.fk_categoria))?.nombre,
              ) || 'Sin categoria';
            if (!nombre) return;
            (activosPorCategoria[categoriaNombre] = activosPorCategoria[categoriaNombre] || []).push(nombre);
          });
          Object.keys(activosPorCategoria).forEach((cat) => {
            activosPorCategoria[cat] = activosPorCategoria[cat].sort((a, b) =>
              a.localeCompare(b, 'es', { sensitivity: 'base' }),
            );
          });
        }

        const categoriasProfesionalesArray = filterCollectionByInstance(
          flattenApiCollection(categoriasProfesionalesData),
          selectedInstanceId,
        );
        const categoriasProfesionalesGruposArray = filterCollectionByInstance(
          flattenApiCollection(categoriasProfesionalesGruposData),
          selectedInstanceId,
        );

        let categoriasProfesionales = {};
        if (categoriasProfesionalesGruposArray.length > 0) {
          categoriasProfesionales = categoriasProfesionalesGruposArray.reduce((acc, grupo) => {
            const grupoNombre = normalizeName(grupo, 'Grupo');
            const profesionales = ensureArray(grupo?.profesionales)
              .map((p) => normalizeName(p))
              .filter(Boolean)
              .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
            if (grupoNombre) {
              acc[grupoNombre] = profesionales;
            }
            return acc;
          }, {});
        } else if (categoriasProfesionalesArray.length > 0) {
          categoriasProfesionales = {
            Profesionales: categoriasProfesionalesArray
              .map((cp) => normalizeName(cp))
              .filter(Boolean)
              .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' })),
          };
        }

        // Tareas y registros
        const tareasGenerales = filterCollectionByInstance(
          flattenApiCollection(tareasRegistrosData),
          selectedInstanceId,
        );
        const tareasChecklistExtendidas = filterCollectionByInstance(
          flattenApiCollection(tareasRegistrosChecklistData),
          selectedInstanceId,
        );
        const tareasArray = mergeTareasCollections(tareasGenerales, tareasChecklistExtendidas);

        const tareasPorCategoriaMap = new Map();
        tareasArray.forEach((entry, index) => {
          const tareaBase = entry?.tarea && typeof entry.tarea === 'object' ? entry.tarea : entry;
          const tareaId =
            firstNonEmpty(
              tareaBase?.id,
              tareaBase?.ID,
              tareaBase?.tarea_id,
              tareaBase?.id_tarea,
              tareaBase?.guid,
              tareaBase?.uuid,
            ) || `tarea-${index}`;
          const tareaNombre = normalizeName(tareaBase, `Tarea ${index + 1}`);
          const fkCategoria = firstNonEmpty(
            tareaBase?.fk_categoria_TR,
            tareaBase?.fk_categoria_tr,
            tareaBase?.fk_categoria,
            tareaBase?.categoria_id,
            tareaBase?.categoriaId,
          );
          const catalogEntry = fkCategoria ? categoriasCatalogo.get(String(fkCategoria)) : null;
          const categoriaNombre =
            catalogEntry?.nombre ||
            firstNonEmpty(entry?.categoria, tareaBase?.categoria, catalogEntry?.nombre) ||
            'Sin categoria';
          const categoriaOrden = catalogEntry?.orden ?? Number.POSITIVE_INFINITY;

          const fkChecklist = firstNonEmpty(entry?.fk_checklist, tareaBase?.fk_checklist, tareaBase?.checklist_id);
          const checklists = ensureArray(entry?.checklists || tareaBase?.checklists || entry?.checklist).map(
            (chk) => ({
              id: firstNonEmpty(chk?.id, chk?.ID, fkChecklist) || '',
              nombre: normalizeName(chk, checklistsCatalog.get(fkChecklist)?.nombre || 'Checklist'),
            }),
          );
          if (fkChecklist && checklists.length === 0) {
            const catalogEntry = checklistsCatalog.get(String(fkChecklist));
            if (catalogEntry) {
              checklists.push(catalogEntry);
            }
          }

          const normalizedTarea = {
            id: String(tareaId),
            name: tareaNombre,
            type: firstNonEmpty(tareaBase?.tipo_datos, tareaBase?.type) || 'ninguno',
            periodicidad: firstNonEmpty(tareaBase?.periodicidad) || 'ninguna',
            dias_semana: firstNonEmpty(tareaBase?.dias_semana, tareaBase?.diasSemana, tareaBase?.dias) || '',
            fk_checklist: fkChecklist || null,
            checklist: checklists.length ? checklists : undefined,
            checklists,
            orden: tareaBase?.orden ?? entry?.orden ?? Number.POSITIVE_INFINITY,
          };

          const categoriaEntrada =
            tareasPorCategoriaMap.get(categoriaNombre) ||
            (() => {
              const value = { nombre: categoriaNombre, orden: categoriaOrden, items: [] };
              tareasPorCategoriaMap.set(categoriaNombre, value);
              return value;
            })();

          categoriaEntrada.items.push(normalizedTarea);
        });

        const tareasPorCategoria = {};
        [...tareasPorCategoriaMap.values()]
          .sort(
            (a, b) =>
              (a.orden ?? Number.POSITIVE_INFINITY) - (b.orden ?? Number.POSITIVE_INFINITY) ||
              a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' }),
          )
          .forEach((cat) => {
            tareasPorCategoria[cat.nombre] = sortByOrderThenName(cat.items);
          });

        setData({
          tipologias,
          equipos,
          categoriasProfesionales,
          planes,
          activosPorCategoria,
          tareas: tareasPorCategoria,
        });
      } catch (err) {
        setError(err.message || 'Error al cargar los datos');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [selectedInstanceId, api]);

  return { isLoading, error, data };
};
